import { Injectable } from '@angular/core';
import { Subject, timer, Subscription, BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';
import { HttpService } from 'modules/shared/services/http.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket: WebSocket;
  private messages: Subject<string>;
  private reconnectInterval: number = 5000;
  private reconnectTimer: Subscription;
  private isManuallyClosed: boolean = false;
  private isConnected: boolean = false;
  public cloudBackendDomain: string = environment.backendApiUrl;
  public activateCall = new BehaviorSubject<boolean>(false);

  constructor(public httpService: HttpService) {
    this.messages = new Subject<string>();
  }

  public connect(url: string): void {
    if (this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket connection already established');
      return;
    }

    console.log('Attempting to connect to WebSocket at URL:', url);
    this.isManuallyClosed = false;
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log('WebSocket connection opened to', url);
      this.isConnected = true;
      this.activateCall.subscribe((res) => {
        if (res && this.isConnected) this.socket.send(JSON.stringify({ callAdminConnected: true }));
      });
    };

    this.socket.onmessage = (event) => {
      const message = event.data;
      console.log('WebSocket message received:', message);
      this.messages.next(message);
      this.handleWebSocketMessage(message);
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.socket.onclose = (event) => {
      console.log('WebSocket connection closed. Code:', event.code, 'Reason:', event.reason);
      this.isConnected = false;
      if (!this.isManuallyClosed && event.code !== 1000) {
        this.reconnect();
      }
    };
  }

  public disconnect(): void {
    this.isManuallyClosed = true;
    this.isConnected = false;
    if (this.socket) {
      console.log('Closing WebSocket connection');
      this.socket.close();
    }
    if (this.reconnectTimer) {
      this.reconnectTimer.unsubscribe();
    }
  }

  private reconnect(): void {
    console.log('Attempting to reconnect in', this.reconnectInterval / 1000, 'seconds');
    // if (!this.reconnectTimer || this.reconnectTimer.closed) {
    this.reconnectTimer = timer(this.reconnectInterval).subscribe(() => {
      if (!this.isManuallyClosed) {
        this.initializeWebsocket();
      }
    });
    // }
  }

  public sendMessage(message: string): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('Sending WebSocket message:', message);
      this.socket.send(message);
    } else {
      console.error('WebSocket is not open. Ready state: ', this.socket.readyState);
    }
  }

  public getMessages(): Subject<string> {
    return this.messages;
  }

  private handleWebSocketMessage(message: string): void {
    let parsedMessage;
    try {
      parsedMessage = JSON.parse(message);
    } catch (e) {
      console.error('Failed to parse message:', message);
      return;
    }
    if (parsedMessage.messageType == 'add_guest') {
      this.showNotification('Kiosk Notification', {
        body: 'New Guest were added to the queue',
        icon: '../../../../../assets/images/headphones.svg',
      });
    }
  }

  showNotification(title: string, options?: NotificationOptions) {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, options);
    }
  }

  async initializeWebsocket() {
    const logindata = JSON.parse(sessionStorage.getItem('adminPortalLoginData'));
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
    const userIdObj = {
      source: 'Admin',
      tenantId: logindata.tenantId,
      propertyId: logindata.propertyId,
      userName: userDetails.username,
    };
    let userId = btoa(JSON.stringify(userIdObj));
    let token = await this.getWebsocketToken(userId, logindata);

    // we make the web socket connection here
    const azurePubSubUrl = environment.azurePubSubUrl;
    if (azurePubSubUrl) this.connect(`${azurePubSubUrl}${token}`);
  }

  async getWebsocketToken(userId: string, logindata: any) {
    if (!environment.proxy) {
      const browserUrl = window.location.protocol + '//' + document.location.hostname;
      this.cloudBackendDomain = browserUrl;
    } else if (this.cloudBackendDomain.includes(',')) {
      this.cloudBackendDomain = this.cloudBackendDomain.split(',')[0];
    }
    let url =
      `${this.cloudBackendDomain}/wbe-kiosk-service/pub-sub/tenants/${logindata.tenantId}/propertyId/${logindata.propertyId}/generateToken?userId=` +
      userId;
    let token: any = '';

    await this.httpService
      .get(url)
      .toPromise()
      .then((data: any) => {
        token = data.token;
      });

    return token;
  }
}
